import { Form } from "../../../system/useForm";
import { Box, Grid, Typography } from "@mui/material";
import FormChoices from "../../../Components/FormChoices";
import FormTextField from "../../../Components/FormTextField";
import FormDateTimePicker from "../../../Components/FormDateTimePicker";
import { AssessmentOrder, Compound, VehicleAddresses } from "../../../system/Domain";
import AssessmentRadioChoice from "./AssessmentRadioChoice";
import FormSelect from "../../../Components/FormSelect";
import { useEffect } from "react";
import { addressSetter, setContactFromCompound } from "../Returns/ReturnOrderUtils";
import useCompanies from "../../../system/useCompanies";
import AssessorCompanyChoice from "../Returns/AssessorCompanyChoice";
import useCompounds from "../../../system/useCompounds";

interface Props {
	form: Form
	addresses: VehicleAddresses
	assessmentOrder?: AssessmentOrder
	compounds: Compound[]
}

const StepAssessment = (props: Props) => {
	const [companies] = useCompanies();
	const [compounds] = useCompounds({ includeAmazonCompounds: false });
	const locationFormField = "assessmentLocationOrigin";
	const locationType = props.form.getValue(locationFormField) ?? "";
	const compoundName = props.form.getValue("compound.name");

	const canEdit = locationType === "CustomEntry";
	const needsContactInformation = locationType !== "CarParkAddress";

	useEffect(() => {
		if (!props.compounds) return;
		if (locationType !== "CarParkAddress") return;

		const compound =
			!!compoundName &&
			!!props.compounds &&
			props.compounds.find((c) => c.name === compoundName);

		if (!compound) {
			props.form.setValue("address", null);
			props.form.setValue("contact", null);
			if (!props.assessmentOrder?.compound && !!props.compounds) {
				const defaultCompound = props.compounds?.find((c) => c.isDefault);
				if (!!defaultCompound) {
					props.form.setValue("compound.name", defaultCompound.name);
				}
			}
		} else {
			addressSetter("address", props.form, compound.address);

			setContactFromCompound("contact", props.form, compound);
		}
	}, [locationType, props.compounds, compoundName]);

	return (
		<Box mb={2}>
			<Box mb={2}>
				<FormChoices
					name="assessmentType"
					label="Welcher Gutachtentyp?"
					form={props.form}
					choices={{
						"Restwertgutachten": "R1 - Restwertgutachten",
						"Minderwertsgutachten": "R2 - Minderwertsgutachten"
					}}
					options={{ required: true }}
				/>
			</Box>
			{companies && (
				<AssessorCompanyChoice
					compounds={compounds}
					form={props.form}
					companies={companies}
					name="company.id"
					assessmentLocation="assessmentLocationOrigin"
					assessmentType="assessmentType"
				/>
			)}
			<Box mb={2}>
				<AssessmentRadioChoice
					name={locationFormField}
					form={props.form}
					isRkv={props.addresses.isRkv}
					aldAddressData={props.addresses}
					required
				/>
			</Box>
			{locationType === "CarParkAddress" && (
				<Box mb={2}>
					<FormSelect
						fullWidth={false}
						form={props.form}
						name="compound.name"
						label="Standort"
						choices={props.compounds
							.map((c) => c.name)
							.reduce((acc, curr) => ((acc[curr] = curr), acc), {})}
					/>
				</Box>
			)}
			<Typography variant="h6" gutterBottom sx={{ marginTop: 2 }}>
				Adresse
			</Typography>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<FormTextField
						form={props.form}
						disabled={!canEdit}
						fullWidth
						name="address.name"
						label="Name"
						required
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormTextField
						form={props.form}
						disabled={!canEdit}
						fullWidth
						name="address.street"
						label="Straße"
						required
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormTextField
						form={props.form}
						disabled={!canEdit}
						fullWidth
						name="address.additionalInformation"
						label="Adresszusatz"
						maxLength={30}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={4}>
					<FormTextField
						form={props.form}
						disabled={!canEdit}
						required
						name="address.zipCode"
						label="PLZ"
						fullWidth
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={4}>
					<FormTextField
						form={props.form}
						disabled={!canEdit}
						required
						name="address.city"
						label="Stadt"
						fullWidth
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={4}>
					<FormTextField
						form={props.form}
						disabled={!canEdit}
						required
						name="address.country"
						label="Land"
						fullWidth
						defaultValue="Deutschland"
					/>
				</Grid>
			</Grid>
			{needsContactInformation && (
				<>
					<Typography variant="h6" gutterBottom sx={{ marginTop: 8 }}>
						Ansprechpartner
					</Typography>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<FormTextField
								form={props.form}
								required
								name="contact.firstName"
								label="Vorname"
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormTextField
								form={props.form}
								required
								name="contact.lastName"
								label="Nachname"
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormTextField
								form={props.form}
								required
								name="contact.email"
								label="E-Mail"
								fullWidth
								email
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormTextField
								required
								form={props.form}
								name="contact.telephone"
								label="Telefon"
								fullWidth
							/>
						</Grid>
					</Grid>
				</>
			)}
			<Typography variant="h6" gutterBottom sx={{ marginTop: 8 }}>
				Wunschtermin
			</Typography>
			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<FormDateTimePicker
						name="appointment.notBefore"
						form={props.form}
						label="ab dem"
						margin="normal"
						variant="date"
						format="DD.MM.YYYY"
						disablePast
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<FormDateTimePicker
						name="appointment.from"
						form={props.form}
						label="Zeit von"
						variant="time"
						ampm={false}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<FormDateTimePicker
						name="appointment.to"
						form={props.form}
						label="bis"
						variant="time"
						ampm={false}
					/>
				</Grid>
			</Grid>
			<Typography variant="h6" gutterBottom sx={{ marginTop: 8 }}>
				Bemerkung zum Gutachten
			</Typography>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<FormTextField
						form={props.form}
						name="comment"
						label="Bemerkung"
						fullWidth
					/>
				</Grid>
			</Grid>
		</Box>
	);
};

export default StepAssessment;
